import React, { useEffect, useMemo, useState } from "react";
import { FaCrown } from "react-icons/fa6";
import { getAvailableCategories } from "../../api/inventory.js";
import DropdownMulti from "../../components/DropdownMulti.jsx";
import TabbedNavigation from "../../components/TabbedNavigation.jsx";
import { saveCategoryRestrictions } from "../../api/admin.js";
import { addToast } from "../../store/app/action.js";
import { useDispatch } from "react-redux";
import { US_STATE_NAMES as states } from "@/utils/usStates.js";

function StoreRestrictionPage() {
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedTab, setSelectedTab] = useState("by-category");
  const dispatch = useDispatch();

  const getCategories = async () => {
    const response = await getAvailableCategories();
    setAvailableCategories(response.categories);
  };
  useEffect(() => {
    getCategories();
  }, []);

  const leafCategories = useMemo(
    () => (availableCategories || []).filter((c) => c.type !== "divider"),
    [availableCategories]
  );

  const categoryOptions = useMemo(
    () =>
      leafCategories.map((c) => ({
        id: c.id,
        label: c.name,
      })),
    [leafCategories]
  );

  const stateCodes = useMemo(() => Object.keys(states), []);

  const handleUpdate = async (id, field, values) => {
    const thisCategory = availableCategories.find((cat) => cat.id === id);
    if (!thisCategory) return;
    thisCategory[field] = values;

    setAvailableCategories((prev) => {
      return prev.map((cat) => {
        if (cat.id === id) {
          return thisCategory;
        }
        return cat;
      });
    });
    await saveCategoryRestrictions(id, thisCategory);
    dispatch(
      addToast({
        type: "success",
        message: `${thisCategory.name} restrictions saved`,
        timer: 1,
      })
    );
  };

  const handleStateColumnUpdate = async (stateCode, fieldName, newCategoryIds) => {
    const oldIds = new Set(
      leafCategories
        .filter((c) => (c[fieldName] || []).includes(stateCode))
        .map((c) => c.id)
    );
    const newIds = new Set(newCategoryIds);
    const toAdd = [...newIds].filter((id) => !oldIds.has(id));
    const toRemove = [...oldIds].filter((id) => !newIds.has(id));

    let next = availableCategories;
    const touched = new Set();

    for (const catId of toAdd) {
      const cat = next.find((c) => c.id === catId);
      if (!cat || cat.type === "divider") continue;
      const arr = [...(cat[fieldName] || [])];
      if (!arr.includes(stateCode)) arr.push(stateCode);
      next = next.map((c) => (c.id === catId ? { ...c, [fieldName]: arr } : c));
      touched.add(catId);
    }
    for (const catId of toRemove) {
      const cat = next.find((c) => c.id === catId);
      if (!cat || cat.type === "divider") continue;
      const arr = (cat[fieldName] || []).filter((s) => s !== stateCode);
      next = next.map((c) => (c.id === catId ? { ...c, [fieldName]: arr } : c));
      touched.add(catId);
    }

    setAvailableCategories(next);
    for (const catId of touched) {
      const data = next.find((c) => c.id === catId);
      if (data) await saveCategoryRestrictions(catId, data);
    }
    if (touched.size) {
      dispatch(
        addToast({
          type: "success",
          message: `Saved restrictions for ${states[stateCode]}`,
          timer: 1,
        })
      );
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      <div className="mb-8">
        <div className="mb-4">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold flex items-center gap-1">
            State Restrictions <FaCrown />
          </h1>
        </div>
        <div className="flex items-center">
          <TabbedNavigation
            tabs={[
              { id: "by-category", label: "By Category / Tag" },
              { id: "by-state", label: "By State" },
            ]}
            activeTab={selectedTab}
            onTabChange={setSelectedTab}
            variant="default"
            size="md"
          />
        </div>
      </div>

      {selectedTab === "by-category" && (
        <table className="table-auto w-full dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
            <tr>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Category</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Delivery Restricted</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">Globally Restricted</div>
              </th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
            {(availableCategories || []).map((category) => {
              if (category.type === "divider") {
                return (
                  <tr key={category.name}>
                    <td
                      className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap text-center"
                      colSpan={3}
                    >
                      <div className="font-semibold text-center">
                        {category.name}
                      </div>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={category.id}>
                  <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">{category.name}</div>
                  </td>
                  <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <DropdownMulti
                      displayKeys
                      setSelectedValues={async (values) => {
                        await handleUpdate(
                          category.id,
                          "direct_delivery_georestriction",
                          values
                        );
                      }}
                      options={stateCodes.map((state) => ({
                        id: state,
                        label: states[state],
                      }))}
                      selectedValues={
                        category.direct_delivery_georestriction || []
                      }
                    />
                  </td>
                  <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <DropdownMulti
                      displayKeys
                      setSelectedValues={async (values) => {
                        await handleUpdate(
                          category.id,
                          "direct_georestriction",
                          values
                        );
                      }}
                      options={stateCodes.map((state) => ({
                        id: state,
                        label: states[state],
                      }))}
                      selectedValues={category.direct_georestriction || []}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {selectedTab === "by-state" && (
        <table className="table-auto w-full dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
            <tr>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">State</div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">
                  Delivery restricted categories
                </div>
              </th>
              <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                <div className="font-semibold text-left">
                  Globally restricted categories
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
            {stateCodes.map((stateCode) => (
              <tr key={stateCode}>
                <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">
                    {states[stateCode]}{" "}
                    <span className="font-normal text-slate-500 dark:text-slate-400">
                      ({stateCode})
                    </span>
                  </div>
                </td>
                <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <DropdownMulti
                    setSelectedValues={async (values) => {
                      await handleStateColumnUpdate(
                        stateCode,
                        "direct_delivery_georestriction",
                        values
                      );
                    }}
                    options={categoryOptions}
                    selectedValues={leafCategories
                      .filter((c) =>
                        (c.direct_delivery_georestriction || []).includes(
                          stateCode
                        )
                      )
                      .map((c) => c.id)}
                  />
                </td>
                <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <DropdownMulti
                    setSelectedValues={async (values) => {
                      await handleStateColumnUpdate(
                        stateCode,
                        "direct_georestriction",
                        values
                      );
                    }}
                    options={categoryOptions}
                    selectedValues={leafCategories
                      .filter((c) =>
                        (c.direct_georestriction || []).includes(stateCode)
                      )
                      .map((c) => c.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default StoreRestrictionPage;
